'use client';

import { useUserStore } from '@unifyapps/carbon/auth/UserStoreProvider';
import { UserSessionMqttSubscriber } from '@unifyapps/carbon/components/UserSessionMqttSubscriber/UserSessionMqttSubscriber';
import { InterfaceNavigationHelper } from '@unifyapps/carbon/helpers/InterfaceNavigationHelper';
import { useSearchParams } from '@unifyapps/carbon/navigation';
import { InterfacePageResourceLoader } from '@unifyapps/carbon/no-code/components/page/InterfacePageResourceLoader';
import { useDeviceCapabilities } from '@unifyapps/carbon/no-code/context/DeviceCapabilitiesContext';
import { useDataColorScheme } from '@unifyapps/carbon/no-code/hooks/useDataColorScheme';
import { useInterfaceResolution } from '@unifyapps/carbon/no-code/hooks/useInterfaceResolution';
import { useGetDeviceDetailsInStateStore } from '@unifyapps/carbon/no-code/stores/GlobalStateStore';
import { InterfaceModes, useInterfaceStore } from '@unifyapps/carbon/no-code/stores/InterfaceStore';
import type { InterfaceComponent } from '@unifyapps/carbon/no-code/types/interfaceComponent';
import { getDimensionClasses } from '@unifyapps/carbon/no-code/utils/getDimensionClasses';
import { stableJsonStringify } from '@unifyapps/carbon/utils/json';
import { type InterfaceEntity, NavigationPosition } from '@unifyapps/defs/types/interface';
import { Box } from '@unifyapps/ui/components/Box';
import ContainerElementContext from '@unifyapps/ui/providers/ContainerElementContext';
import { clsx } from 'clsx';
import { useState } from 'react';
import { AppNavigation } from '../AppNavigation';
import { matrixMode } from '../constants';
import { usePageResourceLoading } from '../hooks/usePageResourceLoading';

export function InterfacePageLayout({
  children,
  renderAppNavigation,
}: {
  children: React.ReactNode;
  renderAppNavigation?: boolean;
}) {
  const [basePageEl, setBasePageEl] = useState<HTMLDivElement | null>(null);
  const isPublic = useInterfaceStore().hooks.useIsPublic();
  const interfaceRecord = useInterfaceStore().hooks.record.useDetails();
  const { device } = useGetDeviceDetailsInStateStore();
  const { shouldAnimate } = useDeviceCapabilities();

  const { resolution } = useInterfaceResolution(basePageEl, device, InterfaceModes.RUNNER);

  const position = InterfaceNavigationHelper.getPositionByResolution(interfaceRecord, resolution);

  const dimensionClasses = getDimensionClasses(device);

  const colorScheme = useDataColorScheme();

  return (
    <Box
      className={clsx('bg-workspace flex print:h-auto', dimensionClasses, {
        // Navigation Bar is rendered first, followed by InterfacePage using 'flex-row'.
        // This works for 'SideNav'.
        // But for 'BottomNav', the order needs to be reversed.
        'flex-col-reverse': position === NavigationPosition.Bottom,
        'flex-col': position === NavigationPosition.Top,
      })}
      data-animations-enabled={String(shouldAnimate)}
      data-color-scheme={colorScheme}
      // Since on server, we rely on User Agent to determine the Navigation resolution,
      // But on client, we make use of screen size.
      ref={setBasePageEl}
      suppressHydrationWarning
    >
      {/* Hide App Navigation for public pages */}
      <ContainerElementContext.Provider value={basePageEl}>
        {/*
         * Navigation visibility depends on the rendering flow:
         * 1. /public/[interfacePageSlug] route — isPublic is true and renderAppNavigation is
         *    not passed, so navigation is hidden for unauthenticated public pages.
         * 2. /[interfacePageSlug] route — for authenticated users viewing a public page type,
         *    renderAppNavigation is explicitly set to control visibility. This allows navigation
         *    to remain visible even on public pages when accessed through the authenticated flow.
         */}
        {renderAppNavigation || !isPublic ? (
          <AppNavigation interfaceRecord={interfaceRecord} resolution={resolution} />
        ) : null}
        {/* If navbar position is bottom, then the navbar should take height configured in styles
      and InterfacePage should take remaining height. If navbar position is not bottom, i.e 
      sidenav/start/end. Give required width to sidenav and InterfacePage should take all the remaining width.
       */}
        <Box
          className={clsx(
            'flex-1',
            {
              'min-h-0':
                position === NavigationPosition.Bottom || position === NavigationPosition.Top,
            },
            {
              'min-w-0':
                position !== NavigationPosition.Bottom && position !== NavigationPosition.Top,
            },
          )}
        >
          {children}
        </Box>
      </ContainerElementContext.Provider>
    </Box>
  );
}

function InterfacePageContainer({
  interfaceRecord,
  children,
  interfaceComponent,
  isPublic,
  pageParams,
  locale,
}: {
  interfaceRecord: InterfaceEntity;
  children: React.ReactNode;
  interfaceComponent: InterfaceComponent;
  isPublic?: boolean;
  pageParams?: Record<string, string | undefined>;
  locale?: string;
}) {
  const searchParams = useSearchParams() as {
    pageInputs?: Record<string, unknown>;
  };
  const userId = useUserStore().hooks.useCurrentUserDetails()?.user?.id;

  const response = usePageResourceLoading({
    interfaceId: interfaceRecord.id,
    interfaceComponent,
    locale,
  });

  return (
    <>
      <InterfacePageResourceLoader
        isPublic={isPublic}
        //this key is used to reset the interface state when the page inputs change
        //i.e. even if the same page is loaded with different inputs, the interface state should be reset
        //stableJsonStringify ensures the key is invariant to property insertion order, avoiding remounts when only key order shifts
        key={stableJsonStringify(searchParams.pageInputs)}
        mode={matrixMode}
        pageParams={pageParams}
        response={response}
      >
        <InterfacePageLayout>{children}</InterfacePageLayout>
      </InterfacePageResourceLoader>
      {/* handle logout from other devices */}
      {userId && <UserSessionMqttSubscriber userId={userId} />}
    </>
  );
}

export default InterfacePageContainer;
